/**
 * useFormCacheSync - brings the simulation form up to date with the React Query
 * cache, which every other user's change reaches (SSE event → refetch → cache).
 *
 * It applies what changed on the server since the last snapshot — a field, a
 * row added or removed, a new order — and nothing else, so what the user has
 * typed but not yet saved stays. It applies it AT ONCE, also while the user is
 * typing (shipcast-nad4), because it never takes the user's place in the form:
 *
 * - Lists on screen change through their own field-array operations (see
 *   `components/form/syncedFieldArray.ts`), so rows keep their keys: the caret,
 *   the unsaved text and the row move together.
 * - The one field the user is in is not overwritten. A server change to it is
 *   held and applied when the user leaves the field — unless the user changed
 *   the field meanwhile, in which case their edit stands (it is saved next).
 * - A list being dragged keeps its rows until the drop, then catches up.
 */

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { SimulationFormHandle } from '@/components/form/SimulationForm';
import type { SimulationFormValues, FeatureFormValues, TeamFormValues, DeliverableFormValues } from '@/types/api';
import { valuesEqual } from '@/lib/utils';

export interface UseFormCacheSyncOptions {
  /**
   * Called after every value the sync writes into the form, with its path —
   * so auto-save can take it as the server's (see `formSyncEvents.ts`).
   */
  onFieldSynced?: (path: string, value: unknown) => void;
}

const SIMULATION_FIELDS = ['name', 'description', 'num_simulations', 'start_date', 'seed'] as const;

/**
 * Syncs simulation data from the React Query cache into the form.
 *
 * @param formRef - The SimulationForm handle
 * @param data - Current simulation data from the cache (undefined while loading)
 */
export function useFormCacheSync(
  formRef: RefObject<SimulationFormHandle | null>,
  data: SimulationFormValues | undefined,
  { onFieldSynced }: UseFormCacheSyncOptions = {},
): void {
  // The server snapshot the form was last brought up to date with.
  const snapshotRef = useRef<SimulationFormValues | undefined>(undefined);
  // Server values for the field the user is in, waiting for them to leave it.
  const heldRef = useRef(new Map<string, HeldValue>());
  // Lists whose changes waited for a drag to end.
  const waitingListsRef = useRef(new Set<string>());

  useEffect(() => {
    const form = formRef.current;
    if (!data || !form) return;
    const snapshot = snapshotRef.current;
    if (snapshot && valuesEqual(snapshot, data)) return;

    if (!snapshot) {
      // The form usually starts from this very data; resetting it anyway would
      // give every row a new key and remount the lists for nothing.
      if (!valuesEqual(form.getValues(), data)) form.reset(data);
    } else {
      const writer = new FormWriter(form, onFieldSynced, heldRef.current, waitingListsRef.current);
      syncList(writer, 'features', snapshot.features, data.features, (path, prev, next) =>
        syncFields(writer, path, `feature:${next.id}`, prev, next, ['id']),
      );
      syncList(writer, 'teams', snapshot.teams, data.teams, (path, prev, next) => {
        syncFields(writer, path, `team:${next.id}`, prev, next, ['id', 'deliverables']);
        syncList(writer, `${path}.deliverables`, prev.deliverables, next.deliverables, (dPath, prevD, nextD) =>
          syncFields(writer, dPath, `deliverable:${next.id}:${nextD.id}`, prevD, nextD, ['id']),
        );
      });
      syncFields(writer, '', 'simulation', snapshot, data, [], SIMULATION_FIELDS);
    }
    snapshotRef.current = structuredClone(data);
  }, [data, formRef, onFieldSynced]);

  // Leaving a field releases what the server changed in it meanwhile.
  useEffect(() => {
    const onFocusOut = () => {
      // Let focus land first: moving into another field of the form is a new
      // field to protect, not a reason to stop protecting the old one.
      setTimeout(() => {
        const form = formRef.current;
        if (!form || heldRef.current.size === 0) return;
        const now = focusedFieldKey(form);
        for (const [key, held] of heldRef.current) {
          if (key === now) continue;
          heldRef.current.delete(key);
          const path = pathOfKey(form, key);
          // The user changed the field: their edit is the newer write.
          if (!path || !valuesEqual(valueAt(form, path), held.base)) continue;
          writeAt(form, path, held.value);
          onFieldSynced?.(path, held.value);
        }
      }, 0);
    };
    document.addEventListener('focusout', onFocusOut);
    return () => document.removeEventListener('focusout', onFocusOut);
  }, [formRef, onFieldSynced]);

  // A drop releases the list's changes that waited for it.
  useEffect(() => {
    const registry = formRef.current?.fieldArrays;
    if (!registry) return;
    return registry.onDrop((path, moved) => {
      const form = formRef.current;
      const snapshot = snapshotRef.current;
      if (!form || !snapshot || !waitingListsRef.current.delete(path)) return;
      const target = listInSnapshot(form, snapshot, path);
      if (!target) return;
      const writer = new FormWriter(form, onFieldSynced, heldRef.current, waitingListsRef.current);
      catchUpList(writer.list(path), target, { order: !moved });
    });
  }, [formRef, onFieldSynced]);
}

// =============================================================================
// Writing into the form
// =============================================================================

interface HeldValue {
  /** The server's new value. */
  value: unknown;
  /** The server's value before it — what the field holds if the user did not edit it. */
  base: unknown;
}

interface Identified {
  id?: string | null;
}

/** Every write the sync makes, guarding the field the user is in and lists being dragged. */
class FormWriter {
  private readonly focusedKey: string | null;

  constructor(
    readonly form: SimulationFormHandle,
    private readonly onFieldSynced: UseFormCacheSyncOptions['onFieldSynced'],
    private readonly held: Map<string, HeldValue>,
    private readonly waitingLists: Set<string>,
  ) {
    this.focusedKey = focusedFieldKey(form);
  }

  /**
   * Write the server's new `value` at `path` (it was `before`), unless `key` is
   * the field the user is in.
   */
  setField(path: string, key: string, value: unknown, before: unknown): void {
    if (key === this.focusedKey) {
      // Several changes may land before the user leaves: the oldest `before`
      // is what the field showed when the user came in.
      this.held.set(key, { value, base: this.held.has(key) ? this.held.get(key)!.base : before });
      return;
    }
    this.held.delete(key);
    writeAt(this.form, path, value);
    this.onFieldSynced?.(path, value);
  }

  /** The list at `path`, changed through its owner's operations when it has one on screen. */
  list(path: string): ListWriter {
    const owner = this.form.fieldArrays.owner(path);
    if (owner?.isDragging()) {
      this.waitingLists.add(path);
      return new WaitingList(this.form, path);
    }
    return owner ? new MountedList(this.form, path, owner.operations) : new UnmountedList(this, path);
  }

  /** Replace a list with no owner on screen. */
  replace(path: string, items: unknown[]): void {
    writeAt(this.form, path, items);
    this.onFieldSynced?.(path, items);
  }
}

/** Structural changes to one list, by entity id. */
interface ListWriter {
  ids(): string[];
  indexOf(id: string): number;
  remove(id: string): void;
  append(item: Identified): void;
  /** Put the rows in `ids` order; rows without an id stay last. */
  reorder(ids: string[]): void;
}

abstract class FormList implements ListWriter {
  constructor(
    protected readonly form: SimulationFormHandle,
    protected readonly path: string,
  ) {}

  protected items(): Identified[] {
    return (valueAt(this.form, this.path) as Identified[] | undefined) ?? [];
  }

  ids(): string[] {
    return this.items().flatMap((item) => (item.id ? [item.id] : []));
  }

  indexOf(id: string): number {
    return this.items().findIndex((item) => item.id === id);
  }

  abstract remove(id: string): void;
  abstract append(item: Identified): void;
  abstract reorder(ids: string[]): void;
}

/** A list on screen: its own operations keep each row's key. */
class MountedList extends FormList {
  constructor(
    form: SimulationFormHandle,
    path: string,
    private readonly operations: {
      move: (from: number, to: number) => void;
      insert: (i: number, v: unknown) => void;
      remove: (i: number) => void;
    },
  ) {
    super(form, path);
  }

  remove(id: string): void {
    const index = this.indexOf(id);
    if (index >= 0) this.operations.remove(index);
  }

  append(item: Identified): void {
    this.operations.insert(this.items().length, item);
  }

  reorder(ids: string[]): void {
    const current = this.items().map((item) => item.id ?? null);
    const wanted = ids.filter((id) => current.includes(id));
    wanted.forEach((id, to) => {
      const from = current.indexOf(id);
      if (from === to) return;
      this.operations.move(from, to);
      current.splice(to, 0, ...current.splice(from, 1));
    });
  }
}

/** A list with nothing on screen to disturb: written whole. */
class UnmountedList extends FormList {
  constructor(
    private readonly writer: FormWriter,
    path: string,
  ) {
    super(writer.form, path);
  }

  remove(id: string): void {
    this.writer.replace(
      this.path,
      this.items().filter((item) => item.id !== id),
    );
  }

  append(item: Identified): void {
    this.writer.replace(this.path, [...this.items(), item]);
  }

  reorder(ids: string[]): void {
    const items = this.items();
    const byId = new Map(items.flatMap((item) => (item.id ? [[item.id, item] as const] : [])));
    const ordered = ids.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []));
    const rest = items.filter((item) => !item.id || !ids.includes(item.id));
    this.writer.replace(this.path, [...ordered, ...rest]);
  }
}

/** A list being dragged: rows stay put until the drop (see `catchUpList`). */
class WaitingList extends FormList {
  remove(): void {}
  append(): void {}
  reorder(): void {}
}

// =============================================================================
// What changed
// =============================================================================

/**
 * Apply the server's changes to one list: rows removed, rows added, each
 * remaining row's fields, and — when the server's order changed — the order.
 * An order the server did not change is left alone, so a move this screen
 * made and the server has not echoed yet is not undone.
 */
function syncList<T extends Identified>(
  writer: FormWriter,
  path: string,
  prev: T[],
  next: T[],
  syncItem: (path: string, prev: T, next: T) => void,
): void {
  const list = writer.list(path);
  const prevById = new Map(prev.flatMap((item) => (item.id ? [[item.id, item] as const] : [])));
  const nextIds = next.flatMap((item) => (item.id ? [item.id] : []));

  for (const id of prevById.keys()) if (!nextIds.includes(id)) list.remove(id);
  for (const item of next) {
    if (item.id && !prevById.has(item.id) && list.indexOf(item.id) < 0) list.append(item);
  }
  for (const item of next) {
    const before = item.id ? prevById.get(item.id) : undefined;
    const index = item.id ? list.indexOf(item.id) : -1;
    if (before && index >= 0) syncItem(`${path}.${index}`, before, item);
  }
  if (!valuesEqual([...prevById.keys()], nextIds)) list.reorder(nextIds);
}

/** After a drop: make the list hold exactly the server's rows, and — unless the user just moved one — its order. */
function catchUpList(list: ListWriter, target: Identified[], { order }: { order: boolean }): void {
  const targetIds = target.flatMap((item) => (item.id ? [item.id] : []));
  for (const id of list.ids()) if (!targetIds.includes(id)) list.remove(id);
  for (const item of target) if (item.id && list.indexOf(item.id) < 0) list.append(item);
  if (order) list.reorder(targetIds);
}

/** Write each of `fields` (default: all of `next`'s keys but `skip`) that changed on the server. */
function syncFields<T extends object>(
  writer: FormWriter,
  basePath: string,
  keyPrefix: string,
  prev: T,
  next: T,
  skip: string[],
  fields: readonly string[] = Object.keys(next),
): void {
  for (const field of fields) {
    if (skip.includes(field)) continue;
    const before = (prev as Record<string, unknown>)[field];
    const after = (next as Record<string, unknown>)[field];
    if (valuesEqual(before, after)) continue;
    writer.setField(basePath ? `${basePath}.${field}` : field, `${keyPrefix}:${field}`, after, before);
  }
}

/** The form value at a path built at run time. */
function valueAt(form: SimulationFormHandle, path: string): unknown {
  return (form.getValues as (path: string) => unknown)(path);
}

function writeAt(form: SimulationFormHandle, path: string, value: unknown): void {
  (form.setValue as (path: string, value: unknown, options: { shouldDirty: boolean }) => void)(path, value, {
    shouldDirty: false,
  });
}

// =============================================================================
// Fields and lists by identity
// =============================================================================

// A field is identified by its row's id, not its index: a reorder applied
// while the user types moves their row to another index.
const FEATURE_FIELD = /^features\.(\d+)\.(.+)$/;
const DELIVERABLE_FIELD = /^teams\.(\d+)\.deliverables\.(\d+)\.(.+)$/;
const TEAM_FIELD = /^teams\.(\d+)\.(.+)$/;

/** The identity of the form field holding the caret, or null. */
function focusedFieldKey(form: SimulationFormHandle): string | null {
  const name = document.activeElement?.getAttribute('name');
  return name ? keyOfPath(form, name) : null;
}

function keyOfPath(form: SimulationFormHandle, path: string): string | null {
  const id = (p: string) => valueAt(form, p) as string | undefined;
  let m = DELIVERABLE_FIELD.exec(path);
  if (m) return `deliverable:${id(`teams.${m[1]}.id`)}:${id(`teams.${m[1]}.deliverables.${m[2]}.id`)}:${m[3]}`;
  m = FEATURE_FIELD.exec(path);
  if (m) return `feature:${id(`features.${m[1]}.id`)}:${m[2]}`;
  m = TEAM_FIELD.exec(path);
  if (m) return `team:${id(`teams.${m[1]}.id`)}:${m[2]}`;
  return (SIMULATION_FIELDS as readonly string[]).includes(path) ? `simulation:${path}` : null;
}

/** Where the field `key` is in the form now, or null when its row is gone. */
function pathOfKey(form: SimulationFormHandle, key: string): string | null {
  const [kind, ...parts] = key.split(':');
  const values = form.getValues();
  const teamIndex = (teamId?: string) => values.teams.findIndex((t) => t.id === teamId);
  if (kind === 'simulation') return parts[0] ?? null;
  if (kind === 'feature') {
    const i = values.features.findIndex((f) => f.id === parts[0]);
    return i < 0 ? null : `features.${i}.${parts[1]}`;
  }
  if (kind === 'team') {
    const i = teamIndex(parts[0]);
    return i < 0 ? null : `teams.${i}.${parts[1]}`;
  }
  if (kind === 'deliverable') {
    const t = teamIndex(parts[0]);
    const d = values.teams[t]?.deliverables.findIndex((x) => x.id === parts[1]) ?? -1;
    return d < 0 ? null : `teams.${t}.deliverables.${d}.${parts[2]}`;
  }
  return null;
}

/** The server's version of the list at form path `path`. */
function listInSnapshot(
  form: SimulationFormHandle,
  snapshot: SimulationFormValues,
  path: string,
): FeatureFormValues[] | TeamFormValues[] | DeliverableFormValues[] | null {
  if (path === 'features') return snapshot.features;
  if (path === 'teams') return snapshot.teams;
  const m = /^teams\.(\d+)\.deliverables$/.exec(path);
  if (!m) return null;
  const teamId = valueAt(form, `teams.${m[1]}.id`) as string | undefined;
  return snapshot.teams.find((t) => t.id === teamId)?.deliverables ?? null;
}
